import { deepEquals } from "#skyward_global";
import { getActionFunction } from "../../skyward.ui.events";
import {
    App,
    Comment,
    Component,
    ComponentOptions,
    ComponentOptionsMixin,
    ComponentOptionsWithObjectProps,
    ComponentPropsOptions,
    ComponentPublicInstance,
    computed,
    ComputedGetter,
    ComputedOptions,
    ComputedRef,
    createApp,
    DefineComponent,
    Directive,
    effectScope,
    EmitsOptions,
    ExtractPropTypes,
    getCurrentScope,
    HTMLAttributes,
    InjectionKey,
    MethodOptions,
    onMounted,
    onScopeDispose,
    onUnmounted,
    Plugin,
    PropType,
    RenderFunction,
    SetupContext,
    VNode,
    VNodeChild,
    watch,
    WatchOptions
} from "vue";
import { CallbackStoreFacade } from "../../types/skyward/events";
import { State } from "../skyVue/core/store/types";

interface PropOptions<T = any>
{
    validator?(value: T): boolean;
}

interface DefaultPropOptions<T> extends PropOptions<T>
{
    default: T extends Function ? T
    : T extends any[] | object ? () => T
    : (T | (() => T));
};

/** A required Vue prop.  This is the type returned by VueProp(). */
export interface RequiredProp<T>
{
    type: PropType<T>;
    required: true;
    validator?(value: unknown): boolean;
}

/** An optional Vue prop.  This is the type returned by OptionalVueProp(). */
export interface OptionalProp<T>
{
    type: PropType<T>;
    required: false;
    default?: null,
    validator?(value: unknown): boolean;
}

/** A Vue prop with a default value.  This is the type returned by DefaultVueProp(). */
export interface DefaultProp<T>
{
    type: PropType<T>;
    required: false;
    default: T;
    validator?(value: unknown): boolean;
}

export type ClassNamesCollection = Record<string, boolean> | string[] | string | null | undefined;

// see https://engineering.tableau.com/really-advanced-typescript-types-c590eee59a12
type ErrorBrand<Err extends string> = Readonly<Record<Err, void>>;

type ForbiddenTypeError<T, U> =
    T extends String ? ErrorBrand<"Cannot use type `String` in argument-less VueProp call"> :
    T extends Number ? ErrorBrand<"Cannot use type `Number` in argument-less VueProp call"> :
    T extends Boolean ? ErrorBrand<"Cannot use type `Boolean` in argument-less VueProp call"> :
    T extends Array<any> ? ErrorBrand<"Cannot use type `Array` in argument-less VueProp call"> :
    T extends Date ? ErrorBrand<"Cannot use type `Date` in argument-less VueProp call"> :
    T extends Function ? ErrorBrand<"Cannot use type `Function` in argument-less VueProp call"> :
    T extends Symbol ? ErrorBrand<"Cannot use type `Symbol` in argument-less VueProp call"> :
    U;

// @ts-expect-error This function is only for testing types, it should remain unused
function _testTypes()
{
    function assert<T>(_arg: Exclude<T, ErrorBrand<any>>) { }
    // VueProp() with no arguments should not allow primitive types, functions, or arrays
    // @ts-expect-error
    assert(VueProp<string>());
    // @ts-expect-error Specific tuple type
    assert(VueProp<[number, number]>());
    // @ts-expect-error Generic array
    assert(VueProp<Array<number>>());
    // @ts-expect-error Generic function
    assert(VueProp<Function>());
    // @ts-expect-error Specific function type
    assert(VueProp<() => void>());
    // @ts-expect-error Optional type
    assert(VueProp<"a" | "b">());
    // @ts-expect-error Optional type includes Object, but also primitives
    // Note that Vue can support such types; this is a limitation in VueProp
    assert(VueProp<string | object>());
    assert(VueProp<{}>());

    // When passed one argument, DefaultVueProp will use it as the default value.
    // It must not be a function, because we can't distinguish it from a type argument or default constructor.
    // It can't be an object or array literal, because Vue doesn't allow those.
    // Finally, it can't be undefined or null, because we need to get its prototype for the type argument
    // (and anyway what's the point, that's just an optional prop)
    assert(DefaultVueProp(1234));
    // @ts-expect-error
    assert(DefaultVueProp([1, 2, 3, 4]));
    // @ts-expect-error
    assert(DefaultVueProp({ hello: "world" }));
    // @ts-expect-error
    assert(DefaultVueProp(String));
    // @ts-expect-error
    assert(DefaultVueProp(undefined));
    // @ts-expect-error
    assert(DefaultVueProp(null));
    // @ts-expect-error
    assert(DefaultVueProp(() => { }));
    assert(DefaultVueProp(String, { default: "hi" }));
    assert(DefaultVueProp(String, { default: null }));
    assert(DefaultVueProp(String, { default: () => "hi" }));
    type Greeting = { hello: string; };
    assert(DefaultVueProp<Greeting>(Object, { default: () => ({ hello: "world" }) }));
    // @ts-expect-error
    assert(DefaultVueProp<Greeting>(Object, { default: { hello: "world" } }));
    // @ts-expect-error
    assert(DefaultVueProp<number[]>(Array, { default: [1, 2, 3, 4] }));
    assert(DefaultVueProp<number[]>(Array, { default: () => [1, 2, 3, 4] }));
    // Special case: default values of function props are always treated as the literal value, not a default constructor,
    // because it's impossible to tell the difference at runtime.  See https://vuejs.org/guide/components/props.html#prop-validation
    assert(DefaultVueProp<() => string>(Function, { default: () => "hi" }));
    // @ts-expect-error
    assert(DefaultVueProp<() => string>(Function, { default: () => () => "hi" }));
}

/**
 * Constructs a required Vue property for the given object type.
 * WARNING: Do not use for the following types: String | Number | Boolean | Array | Date | Function | Symbol
 * @example VueProp()
 * @example VueProp<{ value: string }>()
 */
export function VueProp<T extends object>(): ForbiddenTypeError<T, RequiredProp<T>>;
/**
 * Constructs a required Vue property for the given type.
 *
 * @param type Constructor validator for the type (String | Number | Boolean | Array | Date | Symbol)
 * @param options Vue property options
 * @example VueProp(String)
 * @example VueProp(Number, { validator: val => val % 2 == 0 })
 * @example VueProp<number[]>(Array)
 */
export function VueProp<T>(type: PropType<T>, options?: PropOptions<T>): RequiredProp<T>;
/**
 * Constructs a required Vue property for the given type.
 *
 * @param type Function constructor
 * @param options Vue property options
 * @example VueProp<() => void>(Function)
 */
export function VueProp<T extends Function>(type: FunctionConstructor, options?: PropOptions<T>): RequiredProp<T>;
/**
 * Constructs a required Vue property for multiple types.
 *
 * @param types Array of constructor validators for the types
 * @param options Vue property options
 * @example VueProp([String, Number])
 * @example VueProp<string | number>([String, Number])
 */
export function VueProp<T>(types: PropType<T>[], options?: PropOptions<T>): RequiredProp<T>;

export function VueProp<T>(typeOrTypes: PropType<T> | PropType<T>[] = Object, options?: PropOptions<T>): RequiredProp<T>
{
    const prop = {
        type: Array.isArray(typeOrTypes)
            ? typeOrTypes as any
            : typeOrTypes as unknown as () => T,
        required: true as true,
        default: null,
        validator: options?.validator
    };

    return prop;
}

/**
 * Constructs an optional Vue property for the given object type.
 * WARNING: Do not use for the following types: String | Number | Boolean | Array | Date | Function | Symbol
 * @example OptionalVueProp()
 * @example OptionalVueProp<{ value: string }>()
 */
export function OptionalVueProp<T extends object>(): ForbiddenTypeError<T, OptionalProp<T>>;
/**
 * Constructs an optional Vue property for the given type.
 *
 * @param type Constructor validator for the type (String | Number | Boolean | Array | Date | Symbol)
 * @param options Vue property options
 * @example OptionalVueProp(String)
 * @example OptionalVueProp(Number, { validator: val => val % 2 == 0 })
 */
export function OptionalVueProp<T>(type: PropType<T>, options?: PropOptions<T>): OptionalProp<T>;
/**
 * Constructs an optional Vue property for multiple types, including Function.
 *
 * @param types Array of constructor validators (e.g., [Array, Function])
 * @param options Vue property options
 * @example OptionalVueProp<(() => void) | string[]>([Function, Array])
 */
export function OptionalVueProp<T>(types: (PropType<T> | FunctionConstructor)[], options?: PropOptions<T>): OptionalProp<T>;
/**
 * Constructs an optional Vue property for the given type.
 *
 * @param type Function constructor
 * @param options Vue property options
 * @example OptionalVueProp<() => void>(Function)
 */
export function OptionalVueProp<T extends Function>(type: FunctionConstructor, options?: PropOptions<T>): OptionalProp<T>;
/**
 * Constructs an optional Vue property for multiple types.
 *
 * @param types Array of constructor validators for the types
 * @param options Vue property options
 * @example OptionalVueProp([String, Number])
 * @example OptionalVueProp<string | number>([String, Number], { default: '' })
 */
export function OptionalVueProp<T>(types: PropType<T>[], options?: PropOptions<T>): OptionalProp<T>;

export function OptionalVueProp<T>(typeOrTypes: PropType<T> | PropType<T>[] = Object, options?: PropOptions<T>): OptionalProp<T>
{
    const prop =
    {
        type: Array.isArray(typeOrTypes)
            ? typeOrTypes as any
            : typeOrTypes as unknown as () => T,
        required: false as false,
        default: null,
        validator: options?.validator
    };

    return prop;
}

/**
 * Constructs a Vue property with the given default value.
 *
 * @param defaultValue The default value of the prop.
 * @example DefaultVueProp(0)
 * @example DefaultVueProp({ hello: "world" })
 * @example DefaultVueProp([1,2,3,4])
 */
export function DefaultVueProp<T>(defaultValue: Exclude<T, Function | object | undefined | null>): DefaultProp<T>;
/**
 * Constructs a Vue property for the given type, with a default value and optional validation.
 *
 * @param type Constructor validator for the type (String | Number | Array | Object | Boolean | Date | Function | Symbol)
 * @param options Vue property options
 * @example DefaultVueProp(Number, { default: 0 })
 * @example DefaultVueProp(String, { default() { return "a".repeat(1e9); } })
 * @example DefaultVueProp<number[]>(Array, { default: () => [1,2,3] })
 */
export function DefaultVueProp<T>(type: PropType<T>, options: DefaultPropOptions<T>): DefaultProp<T>;
/**
 * Constructs a Vue property for the given type, with a default value and optional validation.
 *
 * @param type Function constructor
 * @param options Vue property options
 * @example DefaultVueProp<() => void>(Function, { default() { console.log("default function"); } })
 */
export function DefaultVueProp<T>(type: FunctionConstructor, options: DefaultPropOptions<T>): DefaultProp<T>;

export function DefaultVueProp<T>(type: PropType<T> | T = Object, options?: DefaultPropOptions<T>): DefaultProp<T>
{
    let prop: DefaultProp<T>;
    if (typeof type === "function")
    {
        prop =
        {
            type: type as PropType<T>,
            required: false as false,
            default: options!.default as T,
            validator: options!.validator
        };
    }
    else
    {
        prop =
        {
            type: Object.getPrototypeOf(type).constructor,
            required: false as false,
            default: type as T,
            validator: undefined,
        };
    }
    return prop;
}

/**
 * Construct a Vue event emitter declaration for the given type.
 * The emitter will have no validation but will be correctly typed.
 * @example VueEmit<() => boolean>()
 * @example VueEmit<(name: string, index: number) => boolean>()
 */
export function VueEmit<EmitFunction extends (...args: any[]) => void>()
{
    return null as unknown as EmitFunction;
}

/*
 * Example Usages of VueProp()
 * String:      VueProp(String)
 * Number:      VueProp(Number)
 * Boolean:     VueProp(Boolean)
 * Array:       VueProp<Type[]>(Array)
 * Object:      VueProp<Type>()
 * Date:        VueProp(Date)
 * Function:    VueProp<() => void>(Function)
 * Symbol:      VueProp(Symbol)
 *
 * // Note that VueProp<x>(Object) or VueProp<x>() will compile, but will cause a type check fail if x is a String, Number, Boolean, ...
 */

// Original definitions borrowed from defineHtml in vue/runtime-core
// changed DefineComponent<Props> to DefineComponent<Props & HTMLAttributes>
export function defineHtmlComponent<Props, RawBindings = object>(setup: (props: Readonly<Props>, ctx: SetupContext) => RawBindings | RenderFunction): DefineComponent<Props & HTMLAttributes, RawBindings>;
// changed DefineComponent<Props> to DefineComponent<ExtractPropTypes<PropsOptions> & HTMLAttributes>
export function defineHtmlComponent<PropsOptions extends Readonly<ComponentPropsOptions>, RawBindings, D, C extends ComputedOptions = {}, M extends MethodOptions = {}, Mixin extends ComponentOptionsMixin = ComponentOptionsMixin, Extends extends ComponentOptionsMixin = ComponentOptionsMixin, E extends EmitsOptions = Record<string, any>, EE extends string = string>(options: ComponentOptionsWithObjectProps<PropsOptions, RawBindings, D, C, M, Mixin, Extends, E, EE>):
    DefineComponent<ExtractPropTypes<PropsOptions> & HTMLAttributes, RawBindings & HTMLAttributes, D, C, M, Mixin, Extends, E, EE>;

/**
 * Define a Vue component and make all HTML attributes available in JSX, in addition to the component's own props.
 * @param componentDefinition The component's definition object
 */
export function defineHtmlComponent<T>(componentDefinition: T)
{
    return componentDefinition;
}

export type ComponentInstanceOf<T> = T extends Component<infer P, infer B, infer D, infer C, infer M> ? ComponentPublicInstance<P, B, D, C, M> : never;

type SetFunctionReturn<T extends Function, R> = T extends (...args: infer TArgs) => any
    ? (...args: TArgs) => R : never;

// adapted from runtime-core.d.ts
export type EmitsToProps<T extends { [key in string]?: (...args: any[]) => any }> =
    { [K in `on${Capitalize<string & keyof T>}`]: K extends `on${infer C}` ? T[Uncapitalize<C>] extends Function ? SetFunctionReturn<T[Uncapitalize<C>], void> : never : never };

export type EmitPropsOf<T extends Component> = T extends DefineComponent<infer _X, any, any, any, any, any, any, infer E> ? E extends { [key in string]?: (...args: any[]) => any } ? Partial<EmitsToProps<E>> : never : never;
export type PropsOf<T extends Component> = T extends new(...args: any[]) => { $props: infer P } ? P : never;

/** Workaround for a Typescript limitation, to be used in place of ComponentPublicInstance. */
// The default value for $emit is `(name: string, ...args: any[]): void`.
// For types with multiple events defined, TS isn't able to determine that this is a supertype of them.
// For example `((name: "event1", arg: number) => void) | ((name: "event2") => void)` isn't assignable to `(name: string, ...args: any[]) => void`.
export type AnyComponentInstance = Omit<ComponentPublicInstance, "$emit"> & { $emit: Function; };

/** Type of the argument passed to ref methods */
export type RefArgument = AnyComponentInstance | Element | null;
/** Best match for what JSX thinks the type of a ref method is.  This is used when casting to the expected type,
 * so that the casts can be removed if this is fixed. Normally `refMethod` can be used instead.
 */
// If you can make the type definition more specific, please do so!
type JsxRefMethod = (element: object | null) => void;


/**
 * Helper to give component types to ref methods.
 * @example
 * <MyComponent
 *     ref={refMethod<typeof MyComponent>((elem) => console.log(elem?.el))}
 * />
 * @example
 * <div
 *     id={myId}
 *     ref={refMethod<HTMLElement>((elem) => {
 *         if (elem) {
 *             this.myRefs.set(myId, elem);
 *         } else {
 *             this.myRefs.delete(myId);
 *         }
 *     })}
 * @param callback
 */
export function refMethod<T extends Element>(callback: (elem: T | null) => void): JsxRefMethod;
export function refMethod<T extends Component>(callback: (elem: ComponentInstanceOf<T> | null) => void): JsxRefMethod;
export function refMethod(callback: (elem: any) => void): JsxRefMethod
{
    return callback;
}

export function updateRefMap<TKey, TValue extends RefArgument>(map: Map<TKey, TValue>, key: TKey): JsxRefMethod
export function updateRefMap<TKey extends object, TValue extends RefArgument>(map: WeakMap<TKey, TValue>, key: TKey): JsxRefMethod
export function updateRefMap(map: Map<any, object> | WeakMap<any, object>, key: any): JsxRefMethod
{
    return (component: object | null) =>
    {
        if (component !== null)
        {
            map.set(key, component);
        }
        else
        {
            map.delete(key);
        }
    };
}

type AppWithRootComponent<HostElement, TComponent extends Component> = Omit<App<HostElement>, "mount" | "use" | "mixin" | "component" | "directive" | "provide"> & {
    mount(rootContainer: HostElement | string, isHydrate?: boolean, isSVG?: boolean): ComponentInstanceOf<TComponent>;
    use(plugin: Plugin, ...options: any[]): AppWithRootComponent<HostElement, TComponent>;
    mixin(mixin: ComponentOptions): AppWithRootComponent<HostElement, TComponent>;
    component(name: string): Component | undefined;
    component(name: string, component: Component): AppWithRootComponent<HostElement, TComponent>;
    directive(name: string): Directive | undefined;
    directive(name: string, directive: Directive): AppWithRootComponent<HostElement, TComponent>;
    provide<T>(key: InjectionKey<T> | string, value: T): AppWithRootComponent<HostElement, TComponent>;
};
/**
 * Create a Vue app with a specified root component.
 */
export function createAppWithRootComponent<TComponent extends Component>(
    rootComponent: TComponent,
    rootProps?: PropsOf<TComponent> | null
): AppWithRootComponent<Element, TComponent>
{
    return createApp(rootComponent, rootProps as Record<string, unknown> | null | undefined) as unknown as AppWithRootComponent<Element, TComponent>;
}

type PossiblyVNode = VNodeChild | null | (() => (VNodeChild | null));
/**
 * Detect empty VNodes and return null or a specified value.  Vue wraps slot functions to always return a VNode
 * rather than null.  This function detects those vnodes, so that they can be replaced with default values.
 * @param value The value that may contain a vnode
 * @param defaultValue A replacement vnode, or a function to produce one.
 * @example replaceDefaultVNode(this.$slots.default?.()) ?? "No value provided!"
 */
export function replaceDefaultVNode(value: PossiblyVNode, defaultValue: VNodeChild | null = null): VNodeChild | null
{
    if (typeof value === "function")
    {
        value = value();
    }
    if (value === null || value === undefined ||
        (value as VNode).type === Comment ||
        Array.isArray(value) && (value.length === 0 || value.length === 1 && (value as VNode[])[0].type === Comment))
    {
        return defaultValue;
    }
    return value;
}

export function registerCallbackWhileMounted<T extends (...args: any[]) => any>(callbackStore: CallbackStoreFacade<T>, callback: T)
{
    onMounted(() =>
    {
        callbackStore.registerCallback(callback);
    });

    onUnmounted(() =>
    {
        callbackStore.deregisterCallback(callback);
    });
}

export function computedMemo<T>(getter: ComputedGetter<T>, comparerParam?: (new_: T, old: T | undefined) => boolean) : ComputedRef<T>
{
    let oldValue: T | undefined;
    const comparer = comparerParam ?? deepEquals as (new_: any, old: any) => boolean;
    return computed(() =>
    {
        const newValue = getter(oldValue);
        if (!comparer(newValue, oldValue))
        {
            oldValue = newValue;
        }
        return oldValue!;
    });
}

export type WatchMemoOptions<T> = WatchOptions<false> & {
    comparer?: (new_: T, old: T) => boolean;
};

export function watchMemo<T>(getter: () => T, callback: (value: T) => void, options?: WatchMemoOptions<T>)
{
    const comparer = options?.comparer ?? deepEquals as (new_: any, old: any) => boolean;
    return watch(getter, (new_, old) =>
    {
        if (!comparer(new_, old))
        {
            callback(new_);
        }
    }, options);
}

export function addScopedGlobalListener<TType extends string, TTarget>(target: TTarget, events: TType, handler: JQuery.TypeEventHandler<TTarget, undefined, TTarget, TTarget, TType>)
{
    $(target).on(events, handler);
    onScopeDispose(() => $(target).off(events, handler));
}

export function addScopedCallback<T extends (...args: any[]) => any>(callbackStore: CallbackStoreFacade<T>, callback: T)
{
    callbackStore.registerCallback(callback);
    onScopeDispose(() =>
    {
        callbackStore.deregisterCallback(callback);
    });
}

export function onRefreshedOrMounted(state: State<unknown, unknown, unknown>, callback: () => void)
{
    const skyvueScope = getCurrentScope()!;
    // this is an "attached" scope and will automatically be disposed when its parent scope is disposed (ie, when the component is unmounted)
    let refreshScope = effectScope();
    onMounted(() => { refreshScope.run(callback); });
    addScopedCallback(state.onRefresh, () =>
    {
        refreshScope.stop();
        skyvueScope.run(() =>
        {
            refreshScope = effectScope();
            refreshScope.run(callback);
        });
    });
}

export function runLoadActions(loadAction: string | undefined, el: HTMLElement)
{
    loadAction?.split(";").forEach((action: string) =>
    {
        getActionFunction(action)?.apply(el);
    });
}

/**
 * Converts any "class name" collection (string, array, object, or set) into a Set of unique class names.
 * @param classNames The collection of class names to convert.
 * @returns A Set of unique class names.
 */
export function classNamesToUniqueList(classNames?: ClassNamesCollection | Set<string>)
{
    if (classNames === undefined || classNames === null)
    {
        return new Set<string>();
    }
    else if (classNames instanceof Set)
    {
        return new Set(classNames);
    }
    else if (typeof classNames === "string")
    {
        return new Set(classNames.split(" "));
    }
    else if (Array.isArray(classNames))
    {
        return new Set(classNames);
    }
    else if (typeof classNames === "object")
    {
        // Return all keys of the entries where the value is true.
        const enabledClassNames = Object.entries(classNames)
            .filter(([, value]) => value)
            .map(([key]) => key);

        return new Set(enabledClassNames);
    }

    return new Set<string>();
}
